import { accessToken } from "./auth";
import type {
  AdminUser,
  Filters,
  Inquiry,
  InquiryStatus,
  Invitation,
  InvitationRequest,
  InvitationRequestStatus,
  Opportunity,
  OpportunitySubmission,
  OpportunitySubmissionStatus,
} from "./types";

const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(public readonly status: number) {
    super(`API request failed: ${status}`);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit, authenticated = false): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 8_000);
  try {
    const token = authenticated ? await accessToken() : undefined;
    const response = await fetch(`${baseUrl}${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
    if (!response.ok) throw new ApiError(response.status);
    return (await response.json()) as T;
  } finally {
    window.clearTimeout(timeout);
  }
}

export function opportunityApiPath(filters: Filters): string {
  const parameters = new URLSearchParams({ limit: "100" });
  if (filters.query.trim()) parameters.set("q", filters.query.trim());
  if (filters.kind !== "all") parameters.set("kind", filters.kind);
  if (filters.sector !== "all") parameters.set("sector", filters.sector);
  if (filters.country !== "all") parameters.set("country", filters.country);
  return `/api/opportunities?${parameters}`;
}

export async function loadOpportunities(filters: Filters) {
  const response = await request<{ items: Opportunity[] }>(opportunityApiPath(filters));
  return response.items;
}

export function submitOpportunity(payload: Record<string, unknown>) {
  return request<{ id: string; status: "pending" }>("/api/opportunities", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function requestInvitation(payload: Record<string, unknown>) {
  return request<{ status: "pending" }>("/api/invitation-requests", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function submitInquiry(payload: Record<string, unknown>) {
  return request<{ id: string; status: "pending" }>("/api/inquiries", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function geocode(city: string, country: string): Promise<[number, number]> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 5_000);
  try {
    const url = new URL("https://photon.komoot.io/api/");
    url.searchParams.set("q", `${city}, ${country}`);
    url.searchParams.set("limit", "1");
    const response = await fetch(url, { signal: controller.signal, headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error(`Geocoding request failed: ${response.status}`);
    const data = (await response.json()) as {
      features?: Array<{ geometry?: { coordinates?: [number, number] } }>;
    };
    const coordinates = data.features?.[0]?.geometry?.coordinates;
    if (!coordinates || !coordinates.every(Number.isFinite)) throw new Error("Location was not found");
    return coordinates;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function loadAdminQueue() {
  const [me, invitations, invitationRequests, submissions, inquiries] = await Promise.all([
    request<{ user: AdminUser }>("/api/admin/me", undefined, true),
    request<{ items: Invitation[] }>("/api/admin/invitations", undefined, true),
    request<{ items: InvitationRequest[] }>("/api/admin/invitation-requests", undefined, true),
    request<{ items: OpportunitySubmission[] }>("/api/admin/submissions", undefined, true),
    request<{ items: Inquiry[] }>("/api/admin/inquiries", undefined, true),
  ]);
  return {
    user: me.user,
    invitations: invitations.items,
    invitationRequests: invitationRequests.items,
    submissions: submissions.items,
    inquiries: inquiries.items,
  };
}

export async function createInvitation(payload: { company: string; email: string; validDays: number; locale: "de" | "hu" | "en" }) {
  return request<{ item: Invitation; delivery: "sent" }>(
    "/api/admin/invitations",
    { method: "POST", body: JSON.stringify(payload) },
    true,
  );
}

export async function moderateInvitationRequest(id: string, decision: Exclude<InvitationRequestStatus, "pending">, validDays = 14) {
  const response = await request<{ item: InvitationRequest; delivery?: "sent" }>(
    `/api/admin/invitation-requests/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify({ decision, validDays }) },
    true,
  );
  return response.item;
}

export async function moderateOpportunity(id: string, decision: Exclude<OpportunitySubmissionStatus, "pending">) {
  const response = await request<{ item: OpportunitySubmission }>(
    `/api/admin/submissions/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify({ decision }) },
    true,
  );
  return response.item;
}

export async function moderateInquiry(id: string, decision: Exclude<InquiryStatus, "pending">) {
  const response = await request<{ item: Inquiry }>(
    `/api/admin/inquiries/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify({ decision }) },
    true,
  );
  return response.item;
}
